import React from 'react';

// Geometría del cliente (igual que ChromieCraft/aowow): icono 44 px + hueco 14 px
export const CELL = 44;
export const GAP = 14;
export const PITCH = CELL + GAP;
const COLS = 4;
const PAD = 12;

const ICON_URL = (icon) => `https://wow.zamimg.com/images/wow/icons/medium/${icon}.jpg`;
const BORDER_STYLE = { backgroundImage: 'url(/talents/border.gif)' };
const ICON_FALLBACK = (icon) =>
  `/api/proxy/zamimg?url=${encodeURIComponent(ICON_URL(icon))}`;

/**
 * Flechas de requisito. Coordenadas relativas a la cuadrícula; cada segmento es un
 * div con la textura de TalentCalc/arrows (variante "2" = requisito cumplido).
 */
function arrowSegments(talent, req, active) {
  const s = active ? '2' : '';
  const x0 = talent.col * PITCH;
  const y0 = talent.row * PITCH;
  const rx = req.col * PITCH;
  const ry = req.row * PITCH;
  const segs = [];

  if (req.col === talent.col) {
    // Vertical: del borde inferior del requisito al superior del talento
    const top = ry + CELL - 4;
    segs.push({ left: x0 + 14, top, width: 16, height: y0 - top + 8, file: `down${s}`, pos: 'center bottom' });
  } else if (req.row === talent.row) {
    if (req.col < talent.col) {
      const left = rx + CELL - 4;
      segs.push({ left, top: y0 + 14, width: x0 - left + 8, height: 16, file: `right${s}`, pos: 'right center' });
    } else {
      const left = x0 + CELL - 8;
      segs.push({ left, top: y0 + 14, width: rx - left + 4, height: 16, file: `left${s}`, pos: 'left center' });
    }
  } else {
    // En L: tramo horizontal desde el requisito y bajada hasta el talento
    const right = req.col < talent.col;
    const hLeft = right ? rx + CELL - 4 : x0 + 14;
    const hWidth = right ? x0 + 30 - hLeft : rx + 4 - hLeft;
    segs.push({
      left: hLeft, top: ry + 14, width: hWidth, height: 16,
      file: `${right ? 'rightdown' : 'leftdown'}${s}`, pos: right ? 'right center' : 'left center',
    });
    const top = ry + 30;
    segs.push({ left: x0 + 14, top, width: 16, height: y0 - top + 8, file: `down${s}`, pos: 'center bottom' });
  }
  return segs;
}

/**
 * Requisitos sin cumplir (en rojo en el tooltip, como en el juego). Solo cuentan si
 * el talento no tiene puntos: si ya los tiene, por definición se cumplieron.
 */
function unmetRequirements(talent, tree, byId, ranks, points) {
  if ((ranks[talent.id] ?? 0) > 0) return [];
  const out = [];
  const tierPoints = talent.row * 5;
  if (points < tierPoints) out.push(`Requiere ${tierPoints} puntos en talentos de ${tree.name}`);
  const req = talent.req && byId[talent.req.talent];
  if (req && (ranks[req.id] ?? 0) < talent.req.rank) {
    const n = talent.req.rank;
    out.push(`Requiere ${n} ${n === 1 ? 'punto' : 'puntos'} en ${req.name}`);
  }
  return out;
}

function TalentIcon({ talent, rank, requirements, onHover }) {
  const max = talent.ranks.length;
  const state = rank === 0 ? 'empty' : rank === max ? 'full' : 'partial';

  return (
    <div
      className={`talent-cell talent-cell--${state}`}
      style={{ left: talent.col * PITCH, top: talent.row * PITCH }}
      data-testid={`talent-${talent.id}`}
      data-rank={rank}
      onMouseEnter={(e) => onHover({ talent, rank, requirements, el: e.currentTarget })}
      onMouseLeave={() => onHover(null)}
    >
      <img
        src={ICON_URL(talent.icon)}
        alt={talent.name}
        className="talent-cell-icon"
        loading="lazy"
        decoding="async"
        onError={(e) => {
          if (!e.currentTarget.dataset.fallback) {
            e.currentTarget.dataset.fallback = '1';
            e.currentTarget.src = ICON_FALLBACK(talent.icon);
          }
        }}
      />
      <span className="talent-cell-border" style={BORDER_STYLE} />
      <span className="talent-cell-rank">{rank}/{max}</span>
    </div>
  );
}

export default function TalentTree({ tree, ranks, points, onHover }) {
  const rows = Math.max(...tree.talents.map((t) => t.row)) + 1;
  const byId = Object.fromEntries(tree.talents.map((t) => [t.id, t]));
  const gridW = COLS * CELL + (COLS - 1) * GAP;
  const gridH = rows * CELL + (rows - 1) * GAP;

  const arrows = tree.talents
    .filter((t) => t.req && byId[t.req.talent])
    .flatMap((t) => {
      const req = byId[t.req.talent];
      const active = (ranks[req.id] ?? 0) >= t.req.rank;
      return arrowSegments(t, req, active).map((seg, i) => ({ ...seg, key: `${t.id}-${i}` }));
    });

  return (
    <div className="talent-tree" data-testid={`talent-tree-${tree.id}`}>
      <div className="talent-tree-header">
        <img src={ICON_URL(tree.icon)} alt="" className="talent-tree-header-icon" />
        <span className="talent-tree-name">{tree.name}</span>
        <span className="talent-tree-points" data-testid="talent-tree-points">{points}</span>
      </div>
      <div
        className="talent-tree-board"
        style={{
          backgroundImage: `url(${tree.background})`,
          width: gridW + PAD * 2,
          height: gridH + PAD * 2,
        }}
      >
        <div className="talent-tree-grid" style={{ width: gridW, height: gridH, left: PAD, top: PAD }}>
          {arrows.map(({ key, left, top, width, height, file, pos }) => (
            <span
              key={key}
              className="talent-arrow"
              style={{
                left, top, width, height,
                backgroundImage: `url(/talents/arrows/${file}.png)`,
                backgroundPosition: pos,
              }}
            />
          ))}
          {tree.talents.map((t) => (
            <TalentIcon
              key={t.id}
              talent={t}
              rank={ranks[t.id] ?? 0}
              requirements={unmetRequirements(t, tree, byId, ranks, points)}
              onHover={onHover}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
